import { useState, useEffect } from "react";
import { useAuth } from "../../../contexts/AuthContext";
import { useAppDispatch, useAppSelector } from "../../../hooks/redux";
import { 
  fetchUserEvents, 
  selectDashboardState, 
  selectAllUserEvents,
  selectHostedEvents,
  selectAttendingEvents,
  selectUserEvents,
  selectPendingCoHostInvitations,
  clearError,
  // Nuevos imports para conexiones
  fetchUserConnections,
  followUser,
  unfollowUser,
  selectConnections,
  selectConnectionsLoading,
  selectConnectionsError,
  selectFollowingConnections,
  selectFollowersConnections,
  clearConnectionsError
} from "./slice";
import { AuthGuard } from "../../auth/components/AuthGuard";
import { Header } from "../../landing/components/Header";
import { Button } from "../../../components/ui/button";
import { Edit, Bell } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "../../../components/ui/Avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../../../components/ui/tabs";
import { EventsList } from "../components/EventList";
import { ConnectionsList } from "../components/ConnectionList";
import redirectTo from "../../../utils/redirect";
import { ShareModal } from "../../events/components/ShareModal";
import { Loader2 } from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Loading } from "../../../components/ui/Loading";
import { ImportFromLumaModal } from "../../events/components/ImportFromLumaModal";
import { Helmet } from 'react-helmet-async'

const DashboardPage = () => {
    const { isLoading: authLoading, isAuthenticated, isInitialized, user, profile} = useAuth();
    const dispatch = useAppDispatch();
    
    // Redux selectors existentes
    const { isLoading, error, lastFetch } = useAppSelector(selectDashboardState);
    const allEvents = useAppSelector(selectAllUserEvents);
    const hostedEvents = useAppSelector(selectHostedEvents);
    const attendingEvents = useAppSelector(selectAttendingEvents);
    const userEvents = useAppSelector(selectUserEvents);
    const pendingInvitations = useAppSelector(selectPendingCoHostInvitations);
    
    // Nuevos selectors para conexiones
    const connections = useAppSelector(selectConnections);
    const connectionsLoading = useAppSelector(selectConnectionsLoading);
    const connectionsError = useAppSelector(selectConnectionsError);
    const followingConnections = useAppSelector(selectFollowingConnections);
    const followersConnections = useAppSelector(selectFollowersConnections);
    
    const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list');
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [isImportModalOpen, setIsImportModalOpen] = useState(false);
    const [selectedEventForShare, setSelectedEventForShare] = useState<{ title: string; id: string } | null>(null);

    // Fetch events when user is available
    useEffect(() => {
        if (user && (!lastFetch || Date.now() - lastFetch > 5 * 60 * 1000)) {
            dispatch(fetchUserEvents(user.id));
        }
    }, [user, dispatch, lastFetch]);

    // Fetch connections when user is available
    useEffect(() => {
        if (user) {
            dispatch(fetchUserConnections(user.id));
        }
    }, [user, dispatch]);

    // Clear error when component unmounts or user changes
    useEffect(() => {
        return () => {
            dispatch(clearError());
            dispatch(clearConnectionsError());
        };
    }, [dispatch]);

    const currentUser = {
        name: profile?.full_name || user?.email?.split('@')[0] || "User",
        avatarUrl: profile?.avatar_url,
    };

    // Transformar eventos para el componente EventsList
    const transformedEvents = allEvents.map(event => ({
        id: event.id,
        title: event.title,
        description: event.short_description || event.description || '',
        imageUrl: event.image_url || "https://upload.wikimedia.org/wikipedia/commons/thumb/1/1d/Restaurant_in_The_Mus%C3%A9e_d%27Orsay.jpg/960px-Restaurant_in_The_Mus%C3%A9e_d%27Orsay.jpg",
        ecosystem: event.ecosystem || "All ecosystem",
        type: event.user_role === 'host' || event.user_role === 'co-host' ? 'hosted' as const : 
              event.user_role === 'user' ? 'user' as const : 'attending' as const,
        date: event.start_date,
        time: event.start_time,
        location: event.location,
        category: event.category,
        eventType: event.type,
        modality: event.modality,
        hostStatus: event.host_status,
        sideEventsCount: event.side_events?.length || 0,
        parentEventId: event.parent_event_id
    }));

    const handleManageEvent = (eventId: string) => {
        console.log(`Manage event with ID: ${eventId}`);
        redirectTo(`/events/${eventId}`);
    };

    const handleShareEvent = (eventId: string) => {
        const event = allEvents.find(e => e.id === eventId);
        if (event) {
            setSelectedEventForShare({
                title: event.title,
                id: event.id
            });
            setIsShareModalOpen(true);
        }
    };

    // Nuevos handlers para conexiones
    const handleUnfollow = (connectionUserId: string) => {
        if (user) {
            dispatch(unfollowUser({ 
                userFromId: user.id, 
                userToId: connectionUserId 
            }));
        }
    };

    const handleFollow = (connectionUserId: string) => {
        if (user) {
            dispatch(followUser({ 
                userFromId: user.id, 
                userToId: connectionUserId 
            }));
        }
    };

    const handleRefresh = () => {
        if (user) {
            dispatch(fetchUserEvents(user.id));
        }
    };

    const handleRefreshConnections = () => {
        if (user) {
            dispatch(fetchUserConnections(user.id));
        }
    };

    // Separar eventos por tipo
    const hostedEventsTransformed = transformedEvents.filter(e => e.type === 'hosted');
    const attendingEventsTransformed = transformedEvents.filter(e => e.type === 'attending');
    const userEventsTransformed = transformedEvents.filter(e => e.type === 'user');

    // Combinar todas las conexiones para ConnectionsList
    const allConnections = [
        ...followingConnections,
        ...followersConnections
    ];

    return (
        <>
        <Helmet>
            <title>Panel | Punta Tech Meetup</title>
            <meta name="description" content="Gestioná tus eventos, conexiones y perfil dentro de la comunidad Punta Tech Meetup." />
        </Helmet>
        <AuthGuard requireAuth={true} isLoading={authLoading} isAuthenticated={isAuthenticated} isInitialized={isInitialized}>
            <div className="min-h-screen bg-white">
            <Header onSignInClick={() => {}} onImportFromLumaClick={() => setIsImportModalOpen(true)} />
                
            <div className="container mx-auto p-4 md:p-8 mt-16 space-y-8">
                {/* Profile Section */}
                <div className="flex items-center justify-between mb-8 bg-white p-6 rounded-lg shadow">
                    <div className="flex items-center space-x-4">
                        <Avatar className="h-12 w-12">
                            {currentUser.avatarUrl ? (
                                <AvatarImage src={currentUser.avatarUrl} alt={currentUser.name} />
                            ) : (
                                <AvatarFallback className="bg-primary/10 text-primary font-semibold uppercase">
                                    {currentUser.name.charAt(0).toUpperCase()}
                                </AvatarFallback>
                            )}
                        </Avatar>
                        <div className="flex items-center gap-3">
                            <h1 className="text-xl font-semibold">¡Hola, {currentUser.name}!</h1>
                            {pendingInvitations.length > 0 && (
                                <Badge variant="pending" className="flex items-center gap-1">
                                    <Bell className="h-3 w-3" />
                                    {pendingInvitations.length} invitación pendiente{pendingInvitations.length > 1 ? 's' : ''}
                                </Badge>
                            )}
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        <Button 
                            variant="outlinePrimary" 
                            className="flex items-center gap-2 w-full sm:w-auto justify-center"
                            size="sm"
                            onClick={() => redirectTo('/profile/edit')}
                        >
                            <Edit className="h-4 w-4" /> 
                            <span className="hidden sm:inline">Editar perfil</span>
                            <span className="sm:hidden">Editar</span>
                        </Button>
                    </div>
                </div>

                {/* Main Tabs */}
                <Tabs defaultValue="events" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 md:w-1/2 lg:w-1/3 mb-6">
                        <TabsTrigger value="events">Eventos</TabsTrigger>
                        <TabsTrigger value="connections">
                            Conexiones 
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="events" className="bg-white p-6 border rounded-lg">
                        {isLoading ? (
                            <Loading message="Cargando tus eventos..." />
                        ) : error ? (
                            <div className="flex flex-col items-center justify-center py-8">
                                <p className="text-red-600 mb-4">Error cargando eventos: {error}</p>
                                <Button onClick={handleRefresh} variant="outline">
                                    Reintentar
                                </Button>
                            </div>
                        ) : (
                            <EventsList
                                events={transformedEvents}
                                viewMode={viewMode}
                                onViewModeChange={setViewMode}
                                onManageEvent={handleManageEvent}
                                onShareEvent={handleShareEvent}
                                onRefresh={handleRefresh}
                                hostedCount={hostedEventsTransformed.length}
                                attendingCount={attendingEventsTransformed.length}
                                userCount={userEventsTransformed.length}
                            />
                        )}
                    </TabsContent>

                    <TabsContent value="connections" className="bg-white p-6 border rounded-lg">
                        {connectionsLoading ? (
                            <Loading message="Cargando tus conexiones..." />
                        ) : connectionsError ? (
                            <div className="flex flex-col items-center justify-center py-8">
                                <p className="text-red-600 mb-4">Error cargando conexiones: {connectionsError}</p>
                                <Button onClick={handleRefreshConnections} variant="outline">
                                    Reintentar
                                </Button>
                            </div>
                        ) : (
                            <ConnectionsList
                                connections={allConnections}
                                onUnfollow={handleUnfollow}
                                onFollow={handleFollow}
                                followingCount={followingConnections.length}
                                followersCount={followersConnections.length}
                            />
                        )}
                    </TabsContent>
                </Tabs>
            </div>
            </div>

            {/* Modal de compartir */}
            <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                title={selectedEventForShare?.title || "Event"}
                url={`${window.location.origin}/events/${selectedEventForShare?.id}`}
                description="Check out this amazing event!"
            />
        </AuthGuard>
        <ImportFromLumaModal 
            isOpen={isImportModalOpen}
            onClose={() => setIsImportModalOpen(false)}
            userId={user?.id || ''}
            userProfile={{ name: profile?.full_name, email: user?.email || undefined, avatarUrl: profile?.avatar_url || '' }}
            onImported={(eventId) => {
                setIsImportModalOpen(false)
                dispatch(fetchUserEvents(user!.id))
                redirectTo(`/events/${eventId}`)
            }}
        />
        </>
    );
};

DashboardPage.displayName = "Punta Tech Meetup - Panel";
export default DashboardPage;